/**
 * Chat composer, quick actions, message caps, loading copy, and WS message-type strings.
 *
 * Split out of utils/Constants.js (roadmap Module 8) — Constants.js remains
 * a re-export shim until import sites migrate to "src/constants/chat".
 */
// Button click types for Phase 4 features
export const CHAT_BUTTON_TYPES = {
  CONVERT_TO_DRAFT: "convert_to_draft",
  SKIP_TO_DRAFT: "skip_to_draft",
  UNLOCK_DOCUMENT: "unlock_document",
};

export const QUICK_ACTION_BUTTON_TYPES = {
  LEGAL_REVIEW: "legal_review_request",
  QUICK_RESPONSE: "quick_response",
  DRAFT: "draft",
  SKIP_TO_DRAFT: "skip_to_draft",
};

// Quick-action buttons are hidden for messages at or beyond this index to
// reduce context length sent to the AI (cost-saving measure).
export const QUICK_ACTIONS_MAX_MESSAGE_INDEX = 22;

// GPT Loading texts for draft threads
export const GPT_LOADING_TEXTS = {
  IMPORTING: "Importing ChatGPT conversation and preparing summary...",
  ANALYZING: "Analyzing legal issues and preparing additional questions...",
};

// Loading messages
export const loadingMessages = {
  DEFAULT: {
    messages: [
      { text: "Reviewing your request", is_variable: false },
      { text: "Researching", is_variable: false },
      { text: "Searching relevant lawyer notes", is_variable: false },
      { text: "Identifying legal risks", is_variable: false },
      { text: "Identifying legal opportunities", is_variable: false },
    ],
    time_range: 20,
  },
  READING_WEBSITE: {
    // { "url": "https://www.inhouse.ai/", "is_business_website": true}
    messages: [
      { text: `Running web search`, is_variable: false },
      { text: `Finding {url}`, is_variable: true, variable_name: "url" },
      { text: `Identifying key pages`, is_variable: false },
      { text: `Extracting relevant sections`, is_variable: false },
      { text: `Reading recent updates`, is_variable: false },
      { text: `Verifying publication dates`, is_variable: false },
      { text: `Capturing definitions and terms`, is_variable: false },
      { text: `Summarizing key findings`, is_variable: false },
      { text: `Collecting citation links`, is_variable: false },
      { text: `Filtering unrelated results`, is_variable: false },
      { text: `Preparing structured summary`, is_variable: false },
    ],
    time_range: 60,
  },
  PRODUCT_SEARCH: {
    messages: [
      { text: "Understanding your request", is_variable: false },
      { text: "Interpreting pricing question", is_variable: false },
      { text: "Interpreting product question", is_variable: false },
      { text: "Searching pricing page", is_variable: false },
      { text: "Finding product info", is_variable: false },
      { text: "Validating feature availability", is_variable: false },
      { text: "Comparing product details", is_variable: false },
    ],
    time_range: 60,
  },
  ISSUE_SPOTTING: {
    messages: [
      { text: `Understanding your request`, is_variable: false },
      { text: `Detecting document intent`, is_variable: false },
      { text: `Identifying document type`, is_variable: false },
      { text: `Loading {document_type} template`, is_variable: true, variable_name: "document_type" },
      { text: `Determining {document_type} scope`, is_variable: true, variable_name: "document_type" },
      { text: `Selecting jurisdiction scope`, is_variable: false },
      { text: `Checking {jurisdiction} requirements`, is_variable: true, variable_name: "jurisdiction" },
      { text: `Checking required fields`, is_variable: false },
      { text: `Identifying key clauses`, is_variable: false },
      { text: `Listing missing details`, is_variable: false },
      { text: `Applying plain language`, is_variable: false },
      { text: `Aligning to industry norms`, is_variable: false },
      { text: `Estimating review steps`, is_variable: false },
      { text: `Queuing clarifying questions`, is_variable: false },
      { text: `Minimizing questions needed`, is_variable: false },
    ],
    time_range: 60,
  },
  DOC_GENERATION: {
    time_range: 300,
    messages: [
      { text: "Searching relevant lawyer notes", is_variable: false },
      { text: "Running through our lawyer-crafted Playbook", is_variable: false },
      { text: "Generating an outline of the document", is_variable: false },
      { text: "Reviewing 10,000+ legal documents most similar to your agreement", is_variable: false },
      { text: "Seeking opportunities to improve your document", is_variable: false },
      { text: "Reducing unnecessary and redundant legalease", is_variable: false },
      { text: "Generating the final document", is_variable: false },
      { text: "Generating a summary of the document", is_variable: false },
    ],
  },
};

// Per-thread user-message cap for anonymous try-page users only.
// Authenticated users of all tiers have no FE-side message cap.
export const TRY_PAGE_MESSAGE_CAP = 2;

// Free / anonymous user message-limit nudge.
// Backend enforces the hard cap (FREE_MESSAGE_LIMIT_EXHAUSTED).  The FE shows a warning
// banner once the user has sent FREE_MESSAGE_WARNING_THRESHOLD messages.
export const FREE_MESSAGE_WARNING_THRESHOLD = 10;
export const FREE_MESSAGE_LIMIT_EXHAUSTED = 15;


// Subscribed-user per-chat message thresholds (applies to subscriber_business,
// subscriber_enterprise, inhouse_counsel, court_document).
export const HIGH_CONTEXT_WARNING_TYPE = "high_context_warning";
export const SUBSCRIBED_MESSAGE_LIMIT_REACHED_TYPE = "subscribed_message_limit_reached";
export const FREE_USER_ATTACHMENT_LIMIT_REACHED_TYPE = "free_user_attachment_limit_reached";
export const PRIVILEGED_CHAT_STARTED_TYPE = "privileged_chat_started";
export const PRIVILEGED_CHAT_ENDED_TYPE = "privileged_chat_ended";

// Prompt injected into the composer when the user clicks "Summarise this chat".
export const SUMMARIZE_PROMPT =
  "Please give me a concise summary of the key points, decisions, and any open questions from our conversation so far.";

// Signup Alert Popup action types
export const SIGNUP_POPUP_TYPES = {
  START_NEW_CHAT: "start_new_chat", // From "Start new chat" button on try page
  MESSAGE_LIMIT: "message_limit", // When user hits the try-page message cap
};
